import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';

// Simple confetti animation
const triggerConfetti = () => {
  const colors = ['#667eea', '#764ba2', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4'];
  const confettiCount = 150;
  
  for (let i = 0; i < confettiCount; i++) {
    const confetti = document.createElement('div');
    confetti.style.cssText = `
      position: fixed;
      width: ${Math.random() * 10 + 5}px;
      height: ${Math.random() * 10 + 5}px;
      background: ${colors[Math.floor(Math.random() * colors.length)]};
      left: ${Math.random() * 100}vw;
      top: -20px;
      border-radius: ${Math.random() > 0.5 ? '50%' : '0'};
      pointer-events: none;
      z-index: 9999;
      animation: confettiFall ${Math.random() * 3 + 2}s linear forwards;
    `;
    document.body.appendChild(confetti);
    
    setTimeout(() => confetti.remove(), 5000);
  }
  
  // Add keyframes if not exists
  if (!document.getElementById('confetti-styles')) {
    const style = document.createElement('style');
    style.id = 'confetti-styles';
    style.textContent = `
      @keyframes confettiFall {
        to {
          transform: translateY(100vh) rotate(720deg);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }
};

interface HealthProfile {
  id: string;
  height_cm: number;
  target_weight_kg: number;
  date_of_birth: string;
  gender: string;
  activity_level: string;
  health_conditions: string[];
}

interface WeightLog {
  id: string;
  weight_kg: number;
  log_date: string;
  notes: string;
  bmi: number;
}

interface WeightStats {
  current: number;
  change7d: number;
  change30d: number;
  toTarget: number;
  bmiCategory: string;
}

interface HealthDocument {
  id: string;
  document_type: string;
  file_name: string;
  test_date: string;
  is_processed: boolean;
  extracted_data: any;
}

const WeightTracker: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'weight' | 'documents' | 'profile'>('weight');
  const [profile, setProfile] = useState<HealthProfile | null>(null);
  const [weightLogs, setWeightLogs] = useState<WeightLog[]>([]);
  const [stats, setStats] = useState<WeightStats | null>(null);
  const [documents, setDocuments] = useState<HealthDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderTime, setReminderTime] = useState('08:00');
  const [savingReminder, setSavingReminder] = useState(false);

  // Form states
  const [newWeight, setNewWeight] = useState('');
  const [weightDate, setWeightDate] = useState(new Date().toISOString().split('T')[0]);
  const [weightNotes, setWeightNotes] = useState('');
  const [profileForm, setProfileForm] = useState({
    heightCm: '', targetWeightKg: '', dateOfBirth: '', gender: '', activityLevel: 'moderate', healthConditions: ''
  });
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState('checkup_report');
  const [testDate, setTestDate] = useState('');
  const [uploading, setUploading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [weightRes, docsRes, profileRes, reminderRes] = await Promise.all([
        apiService.getWeightLogs(),
        apiService.getHealthDocuments(),
        apiService.getHealthProfile(),
        apiService.getWeightReminderSettings()
      ]);

      if (weightRes.success) {
        setWeightLogs(weightRes.logs || []);
        setStats(weightRes.stats);
        if (weightRes.profile) setProfile(weightRes.profile);
      }
      if (docsRes.success) setDocuments(docsRes.documents || []);
      if (profileRes.success && profileRes.profile) {
        setProfile(profileRes.profile);
        setProfileForm({
          heightCm: profileRes.profile.height_cm?.toString() || '',
          targetWeightKg: profileRes.profile.target_weight_kg?.toString() || '',
          dateOfBirth: profileRes.profile.date_of_birth || '',
          gender: profileRes.profile.gender || '',
          activityLevel: profileRes.profile.activity_level || 'moderate',
          healthConditions: profileRes.profile.health_conditions?.join(', ') || ''
        });
      }
      if (reminderRes.success && reminderRes.settings) {
        setReminderEnabled(reminderRes.settings.enabled);
        setReminderTime(reminderRes.settings.time || '08:00');
      }
    } catch (error) {
      console.error('Failed to fetch health data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Check if profile needs to be set up
  useEffect(() => {
    if (!loading && !profile) {
      setShowProfileModal(true);
    }
  }, [loading, profile]);

  const handleSaveReminderSettings = async () => {
    setSavingReminder(true);
    try {
      await apiService.updateWeightReminderSettings({ enabled: reminderEnabled, time: reminderTime });
      alert(reminderEnabled ? `✅ Daily reminder set for ${reminderTime}!` : '✅ Reminder disabled');
    } catch (error) {
      console.error('Failed to save reminder settings:', error);
    } finally {
      setSavingReminder(false);
    }
  };

  const handleLogWeight = async () => {
    if (!newWeight || parseFloat(newWeight) <= 0) return;
    try {
      const newWeightKg = parseFloat(newWeight);
      const response = await apiService.logWeight({
        weightKg: newWeightKg,
        logDate: weightDate,
        notes: weightNotes
      });
      if (response.success) {
        // Check if user reached their target weight
        const targetWeight = profile?.target_weight_kg;
        const previousWeight = stats?.current;
        
        if (targetWeight && previousWeight && newWeightKg <= targetWeight && previousWeight > targetWeight) {
          // User just hit their goal!
          triggerConfetti();
          setTimeout(() => {
            alert('🎉 Congratulations! You have reached your target weight goal! Amazing achievement!');
          }, 500);
        } else if (targetWeight && newWeightKg <= targetWeight) {
          // User is at or below target
          triggerConfetti();
        }
        
        setWeightLogs([response.log, ...weightLogs.filter(l => l.log_date !== weightDate)]);
        setNewWeight('');
        setWeightNotes('');
        fetchData(); // Refresh stats
      }
    } catch (error) {
      console.error('Failed to log weight:', error);
    }
  };

  const handleSaveProfile = async () => {
    try {
      const response = await apiService.saveHealthProfile({
        heightCm: profileForm.heightCm ? parseFloat(profileForm.heightCm) : null,
        targetWeightKg: profileForm.targetWeightKg ? parseFloat(profileForm.targetWeightKg) : null,
        dateOfBirth: profileForm.dateOfBirth || null,
        gender: profileForm.gender || null,
        activityLevel: profileForm.activityLevel || null,
        healthConditions: profileForm.healthConditions ? profileForm.healthConditions.split(',').map(c => c.trim()).filter(c => c) : null
      });
      if (response.success) {
        setProfile(response.profile);
        setShowProfileModal(false);
        fetchData();
      }
    } catch (error) {
      console.error('Failed to save profile:', error);
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('documentType', uploadType);
      if (testDate) formData.append('testDate', testDate);

      const response = await apiService.uploadHealthDocument(formData);
      if (response.success) {
        setDocuments([response.document, ...documents]);
        setShowUploadModal(false);
        setUploadFile(null);
        setTestDate('');
        if (response.document.extracted_data) {
          alert('✅ Document processed! Health metrics extracted successfully.');
        }
      }
    } catch (error) {
      console.error('Failed to upload document:', error);
      alert('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteWeight = async (logId: string) => {
    if (!confirm('Delete this weight entry?')) return;
    try {
      await apiService.deleteWeightLog(logId);
      setWeightLogs(weightLogs.filter(l => l.id !== logId));
      fetchData();
    } catch (error) {
      console.error('Failed to delete weight log:', error);
    }
  };

  const getBmiColor = (bmi: number) => {
    if (bmi < 18.5) return '#3b82f6';
    if (bmi < 25) return '#22c55e';
    if (bmi < 30) return '#f59e0b';
    return '#ef4444';
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Calculate graph data
  const graphData = [...weightLogs].reverse().slice(-30);
  const minWeight = Math.min(...graphData.map(l => l.weight_kg)) - 2;
  const maxWeight = Math.max(...graphData.map(l => l.weight_kg)) + 2;
  const weightRange = maxWeight - minWeight || 1;

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading health data...</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '2rem' }}>⚖️</span> Weight Tracker
          </h1>
          <p style={{ color: '#6b7280', marginTop: '4px' }}>Track your weight, BMI, and health metrics</p>
        </div>
        <button
          onClick={() => setShowProfileModal(true)}
          style={{
            padding: '10px 16px',
            backgroundColor: '#f3f4f6',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          ⚙️ Profile
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '2px solid #e5e7eb', paddingBottom: '8px' }}>
        {[
          { key: 'weight', label: '⚖️ Weight', icon: '' },
          { key: 'documents', label: '📄 Health Docs', icon: '' },
          { key: 'profile', label: '👤 Profile', icon: '' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            style={{
              padding: '10px 20px',
              backgroundColor: activeTab === tab.key ? '#667eea' : 'transparent',
              color: activeTab === tab.key ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Weight Tab */}
      {activeTab === 'weight' && (
        <div>
          {/* Stats Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{ padding: '20px', backgroundColor: '#f0f4ff', borderRadius: '12px' }}>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '4px' }}>Current Weight</div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: '#667eea' }}>
                {stats?.current ? `${stats.current} kg` : '--'}
              </div>
              {stats?.bmiCategory && (
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '4px' }}>
                  BMI: {weightLogs[0]?.bmi} ({stats.bmiCategory})
                </div>
              )}
            </div>

            <div style={{ padding: '20px', backgroundColor: stats?.change7d && stats.change7d < 0 ? '#f0fdf4' : '#fef2f2', borderRadius: '12px' }}>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '4px' }}>7-Day Change</div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: stats?.change7d && stats.change7d < 0 ? '#22c55e' : '#ef4444' }}>
                {stats?.change7d !== null && stats?.change7d !== undefined ? `${stats.change7d > 0 ? '+' : ''}${stats.change7d} kg` : '--'}
              </div>
            </div>

            <div style={{ padding: '20px', backgroundColor: stats?.change30d && stats.change30d < 0 ? '#f0fdf4' : '#fef2f2', borderRadius: '12px' }}>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '4px' }}>30-Day Change</div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: stats?.change30d && stats.change30d < 0 ? '#22c55e' : '#ef4444' }}>
                {stats?.change30d !== null && stats?.change30d !== undefined ? `${stats.change30d > 0 ? '+' : ''}${stats.change30d} kg` : '--'}
              </div>
            </div>

            <div style={{ padding: '20px', backgroundColor: '#fefce8', borderRadius: '12px' }}>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '4px' }}>To Target</div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: '#f59e0b' }}>
                {stats?.toTarget !== null && stats?.toTarget !== undefined ? `${stats.toTarget > 0 ? '+' : ''}${stats.toTarget} kg` : '--'}
              </div>
              {profile?.target_weight_kg && (
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '4px' }}>
                  Target: {profile.target_weight_kg} kg
                </div>
              )}
            </div>
          </div>

          {/* Log Weight Form */}
          <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', marginBottom: '24px' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '16px' }}>📝 Log Today&apos;s Weight</h3>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '4px' }}>Weight (kg)</label>
                <input
                  type="number"
                  step="0.1"
                  value={newWeight}
                  onChange={e => setNewWeight(e.target.value)}
                  placeholder="e.g., 70.5"
                  style={{ width: '120px', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '4px' }}>Date</label>
                <input
                  type="date"
                  value={weightDate}
                  onChange={e => setWeightDate(e.target.value)}
                  style={{ padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
              </div>
              <div style={{ flex: 1, minWidth: '150px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '4px' }}>Notes (optional)</label>
                <input
                  type="text"
                  value={weightNotes}
                  onChange={e => setWeightNotes(e.target.value)}
                  placeholder="e.g., After workout"
                  style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
              </div>
              <button
                onClick={handleLogWeight}
                disabled={!newWeight}
                style={{
                  padding: '10px 24px',
                  backgroundColor: newWeight ? '#667eea' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: newWeight ? 'pointer' : 'not-allowed'
                }}
              >
                Log Weight
              </button>
            </div>
          </div>

          {/* Weight Graph */}
          {graphData.length > 1 && (
            <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '16px' }}>📈 Weight Trend</h3>
              <div style={{ height: '200px', display: 'flex', alignItems: 'flex-end', gap: '4px', paddingBottom: '30px', position: 'relative' }}>
                {/* Y-axis labels */}
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: '30px', width: '50px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{maxWeight.toFixed(1)}</span>
                  <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{((maxWeight + minWeight) / 2).toFixed(1)}</span>
                  <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{minWeight.toFixed(1)}</span>
                </div>
                {/* Bars */}
                <div style={{ marginLeft: '55px', flex: 1, display: 'flex', alignItems: 'flex-end', gap: '4px', height: '100%' }}>
                  {graphData.map((log, idx) => {
                    const height = ((log.weight_kg - minWeight) / weightRange) * 100;
                    return (
                      <div key={log.id} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div
                          style={{
                            width: '100%',
                            maxWidth: '30px',
                            height: `${height}%`,
                            backgroundColor: getBmiColor(log.bmi || 22),
                            borderRadius: '4px 4px 0 0',
                            minHeight: '4px'
                          }}
                          title={`${log.weight_kg} kg - ${formatDate(log.log_date)}`}
                        />
                        {(idx === 0 || idx === graphData.length - 1 || idx % 7 === 0) && (
                          <span style={{ fontSize: '0.6rem', color: '#9ca3af', marginTop: '4px', whiteSpace: 'nowrap' }}>
                            {new Date(log.log_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Weight History */}
          <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '16px' }}>📋 Weight History</h3>
            {weightLogs.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#9ca3af', padding: '20px' }}>No weight entries yet. Start logging!</p>
            ) : (
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {weightLogs.slice(0, 20).map(log => (
                  <div key={log.id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    borderBottom: '1px solid #f3f4f6'
                  }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{log.weight_kg} kg</div>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{formatDate(log.log_date)}</div>
                      {log.notes && <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{log.notes}</div>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {log.bmi && (
                        <span style={{
                          padding: '4px 8px',
                          backgroundColor: getBmiColor(log.bmi) + '20',
                          color: getBmiColor(log.bmi),
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          fontWeight: 600
                        }}>
                          BMI: {log.bmi}
                        </span>
                      )}
                      <button
                        onClick={() => handleDeleteWeight(log.id)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0 }}>📄 Health Documents</h3>
            <button
              onClick={() => setShowUploadModal(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              + Upload Document
            </button>
          </div>

          {documents.length === 0 ? (
            <div style={{
              padding: '60px',
              textAlign: 'center',
              backgroundColor: '#f9fafb',
              borderRadius: '12px',
              border: '2px dashed #e5e7eb'
            }}>
              <p style={{ fontSize: '3rem', marginBottom: '12px' }}>📋</p>
              <p style={{ color: '#6b7280', marginBottom: '8px' }}>No health documents uploaded</p>
              <p style={{ fontSize: '0.85rem', color: '#9ca3af' }}>Upload blood tests, checkup reports, prescriptions</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {documents.map(doc => (
                <div key={doc.id} style={{
                  padding: '16px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span>{doc.is_processed ? '✅' : '⏳'}</span>
                        <span style={{ fontWeight: 600 }}>{doc.file_name}</span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                        {doc.document_type.replace('_', ' ')} · {doc.test_date ? formatDate(doc.test_date) : 'No date'}
                      </div>
                    </div>
                  </div>

                  {/* Extracted Metrics */}
                  {doc.extracted_data?.metrics && doc.extracted_data.metrics.length > 0 && (
                    <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '8px' }}>Extracted Metrics:</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {doc.extracted_data.metrics.slice(0, 6).map((metric: any, idx: number) => (
                          <div key={idx} style={{
                            padding: '6px 10px',
                            backgroundColor: 'white',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            border: `1px solid ${metric.status === 'normal' ? '#22c55e' : metric.status === 'high' ? '#ef4444' : '#f59e0b'}`
                          }}>
                            <span style={{ fontWeight: 600 }}>{metric.name}:</span> {metric.value} {metric.unit}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div style={{ maxWidth: '600px' }}>
          <div style={{ padding: '24px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '20px' }}>👤 Health Profile</h3>

            {profile ? (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Height</div>
                    <div style={{ fontWeight: 600 }}>{profile.height_cm ? `${profile.height_cm} cm` : 'Not set'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Target Weight</div>
                    <div style={{ fontWeight: 600 }}>{profile.target_weight_kg ? `${profile.target_weight_kg} kg` : 'Not set'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Date of Birth</div>
                    <div style={{ fontWeight: 600 }}>{profile.date_of_birth ? formatDate(profile.date_of_birth) : 'Not set'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Gender</div>
                    <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{profile.gender || 'Not set'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Activity Level</div>
                    <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{profile.activity_level?.replace('_', ' ') || 'Not set'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Health Conditions</div>
                    <div style={{ fontWeight: 600 }}>
                      {profile.health_conditions?.length > 0 ? profile.health_conditions.join(', ') : 'None'}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowProfileModal(true)}
                  style={{
                    marginTop: '20px',
                    padding: '10px 20px',
                    backgroundColor: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Edit Profile
                </button>
              </div>
            ) : (
              <p style={{ color: '#9ca3af' }}>Profile not set up yet.</p>
            )}
          </div>

          {/* Weight Reminder Settings */}
          <div style={{ padding: '24px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', marginTop: '16px' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '20px' }}>🔔 Daily Weight Reminder</h3>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={reminderEnabled}
                  onChange={e => setReminderEnabled(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>Enable daily reminder</span>
              </label>
            </div>

            {reminderEnabled && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>
                  Reminder Time
                </label>
                <input
                  type="time"
                  value={reminderTime}
                  onChange={e => setReminderTime(e.target.value)}
                  style={{ padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>
                  You&apos;ll receive a notification at this time to log your weight
                </p>
              </div>
            )}

            <button
              onClick={handleSaveReminderSettings}
              disabled={savingReminder}
              style={{
                padding: '10px 20px',
                backgroundColor: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: savingReminder ? 'not-allowed' : 'pointer',
                opacity: savingReminder ? 0.7 : 1
              }}
            >
              {savingReminder ? 'Saving...' : 'Save Reminder Settings'}
            </button>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '450px' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '20px' }}>👤 Health Profile Setup</h2>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Height (cm)</label>
                <input
                  type="number"
                  value={profileForm.heightCm}
                  onChange={e => setProfileForm({ ...profileForm, heightCm: e.target.value })}
                  placeholder="e.g., 170"
                  style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Target Weight (kg)</label>
                <input
                  type="number"
                  value={profileForm.targetWeightKg}
                  onChange={e => setProfileForm({ ...profileForm, targetWeightKg: e.target.value })}
                  placeholder="e.g., 65"
                  style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Date of Birth</label>
                <input
                  type="date"
                  value={profileForm.dateOfBirth}
                  onChange={e => setProfileForm({ ...profileForm, dateOfBirth: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Gender</label>
                <select
                  value={profileForm.gender}
                  onChange={e => setProfileForm({ ...profileForm, gender: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                >
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Activity Level</label>
              <select
                value={profileForm.activityLevel}
                onChange={e => setProfileForm({ ...profileForm, activityLevel: e.target.value })}
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              >
                <option value="sedentary">Sedentary (little/no exercise)</option>
                <option value="light">Light (1-3 days/week)</option>
                <option value="moderate">Moderate (3-5 days/week)</option>
                <option value="active">Active (6-7 days/week)</option>
                <option value="very_active">Very Active (athlete)</option>
              </select>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Health Conditions</label>
              <input
                type="text"
                value={profileForm.healthConditions}
                onChange={e => setProfileForm({ ...profileForm, healthConditions: e.target.value })}
                placeholder="e.g., Diabetes, Hypertension (comma separated)"
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowProfileModal(false)} style={{ padding: '10px 20px', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleSaveProfile} style={{ padding: '10px 20px', backgroundColor: '#667eea', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
                Save Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '450px' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '20px' }}>📄 Upload Health Document</h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Document Type</label>
              <select
                value={uploadType}
                onChange={e => setUploadType(e.target.value)}
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              >
                <option value="blood_test">Blood Test Report</option>
                <option value="checkup_report">General Checkup</option>
                <option value="prescription">Prescription</option>
                <option value="xray">X-Ray / Scan</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Test Date</label>
              <input
                type="date"
                value={testDate}
                onChange={e => setTestDate(e.target.value)}
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '8px' }}>Select PDF File</label>
              <input
                type="file"
                accept=".pdf"
                onChange={e => setUploadFile(e.target.files?.[0] || null)}
                style={{ width: '100%' }}
              />
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '8px' }}>
                🤖 AI will automatically extract health metrics from your document
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowUploadModal(false)} style={{ padding: '10px 20px', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                onClick={handleUploadDocument}
                disabled={!uploadFile || uploading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: uploadFile && !uploading ? '#667eea' : '#d1d5db',
                  color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600,
                  cursor: uploadFile && !uploading ? 'pointer' : 'not-allowed'
                }}
              >
                {uploading ? 'Processing...' : 'Upload & Analyze'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeightTracker;
